import React, { useEffect, useState } from 'react';
import {
  CalendarDays, Clock, MapPin, Download, FileText, ArrowRight, Briefcase, GraduationCap,
  HeartPulse, Droplet, Activity, Ribbon, Heart, Trophy, LucideIcon, Loader2,
} from 'lucide-react';
import SafeImage from '../components/SafeImage';
import TiltCard from '../components/TiltCard';
import { StaggerGroup, StaggerItem } from '../components/Stagger';
import EventRegisterModal from '../components/EventRegisterModal';
import JobApplyModal from '../components/JobApplyModal';

// Deployed Web app URL from announcements-webhook.gs — one URL now returns
// events, blogs, downloads, and jobs together in a single response.
const ANNOUNCEMENTS_API_URL = 'https://script.google.com/macros/s/AKfycbwu626NXKCjEQRkAvVbnN3RihEjOzhlIBz206pxIDJJsjkey95fQTKCDV-CN4uVH6vPwA/exec';
// Apps Script web apps can take several seconds to respond, especially on a
// cold start — that's on Google's infrastructure, not something fixable
// from here. Caching the result means only the *first* visit in a session
// pays that cost; repeat visits/refreshes within CACHE_TTL_MS show instantly
// from cache while quietly refreshing in the background.
const CACHE_KEY = 'reach_announcements_cache_v1';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
interface RemoteEvent {
  id?: string;
  title: string;
  date: string;
  time?: string;
  venue: string;
  category: string;
  type: 'upcoming' | 'past';
  discount?: string;
  image: string;
  description: string;
}

interface RemoteBlog {
  title: string;
  date: string;
  read: string;
  tag: string;
  url?: string;
}

interface RemoteDownload {
  title: string;
  size: string;
  fileurl: string;
}

interface RemoteJob {
  title: string;
  qualification: string;
  experience?: string;
  image?: string;
}

// The sheet only stores plain text/URLs, not React icon components — these
// pick a reasonable icon based on text your client typed in, falling back
// to a generic one if nothing matches.
const pickEventIcon = (category: string): LucideIcon => {
  const c = (category || '').toLowerCase();
  if (c.includes('blood')) return Droplet;
  if (c.includes('awareness')) return Ribbon;
  if (c.includes('workshop')) return Trophy;
  if (c.includes('community')) return Heart;
  if (c.includes('cardio') || c.includes('heart')) return Heart;
  return HeartPulse;
};

const pickBlogIcon = (tag: string): LucideIcon => {
  const t = (tag || '').toLowerCase();
  if (t.includes('sport')) return Trophy;
  if (t.includes('community')) return Heart;
  if (t.includes('blood')) return Droplet;
  return Activity;
};

// Some sheet dates come through as a raw ISO timestamp (e.g. if the cell
// was typed/parsed as a real date+time rather than plain text) — this
// catches that and reformats it into something readable, and otherwise
// just shows whatever text was in the cell as-is.
const formatEventDate = (raw: string): string => {
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    }
  }
  return raw;
};

const Announcements: React.FC = () => {
  const [tab, setTab] = useState<'upcoming' | 'jobs'>('upcoming');
  const [registerEvent, setRegisterEvent] = useState<{ id: string; title: string } | null>(null);

  const [events, setEvents] = useState<RemoteEvent[]>([]);
  const [blogs, setBlogs] = useState<RemoteBlog[]>([]);
  const [downloads, setDownloads] = useState<RemoteDownload[]>([]);
  const [jobs, setJobs] = useState<RemoteJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // One fetch for the whole page — events, blogs, downloads, and jobs all
  // load together, so the page reveals itself once instead of each section
  // popping in separately at different times. Cached in sessionStorage so
  // repeat visits within CACHE_TTL_MS skip waiting on Apps Script entirely.
  useEffect(() => {
    let cancelled = false;
    let hasCachedData = false;

    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { events: cE, blogs: cB, downloads: cD, jobs: cJ, ts } = JSON.parse(cached);
        setEvents(cE); setBlogs(cB); setDownloads(cD); setJobs(cJ || []);
        setLoading(false);
        hasCachedData = true;
        if (Date.now() - ts < CACHE_TTL_MS) return; // fresh enough, skip refetch
      }
    } catch {
      // corrupt/missing cache — just fall through and fetch normally
    }

    // Apps Script web app responses go through a redirect to a temporary
    // usercontent.google.com URL, and browsers can end up caching that
    // redirect even though the temp URL it points to has since expired —
    // that's what made this work once and then silently fail on the next
    // in-app visit. Cache-busting the URL and disabling the fetch cache
    // stops the browser from ever reusing that stale redirect.
    fetch(`${ANNOUNCEMENTS_API_URL}?t=${Date.now()}`, { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data?.success) {
          setEvents(Array.isArray(data.events) ? data.events : []);
          setBlogs(Array.isArray(data.blogs) ? data.blogs : []);
          setDownloads(Array.isArray(data.downloads) ? data.downloads : []);
          setJobs(Array.isArray(data.jobs) ? data.jobs : []);
          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({
              events: data.events, blogs: data.blogs, downloads: data.downloads, jobs: data.jobs, ts: Date.now(),
            }));
          } catch {
            // storage full or blocked — not worth failing the page over
          }
        } else if (!hasCachedData) {
          // Only show the hard error when there's genuinely nothing to
          // fall back on — if we already have (possibly slightly stale)
          // cached content on screen, just keep showing that instead of
          // blanking the page over a refresh that failed in the background.
          setLoadError(true);
        }
      })
      .catch(() => { if (!cancelled && !hasCachedData) setLoadError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const filteredEvents = events.filter(e => (e.type || '').toLowerCase() === 'upcoming');
  const [applyJob, setApplyJob] = useState<string | null>(null);

  return (
    <>
      <section className="grain" style={{ paddingTop: 130, paddingBottom: 60, background: 'linear-gradient(150deg, #eef8f9 0%, #fff 55%, #fdf1f1 100%)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 className="section-title">Announcements & Events</h1>
          <p className="section-subtitle" style={{ margin: '0 auto' }}>
            Health camps, awareness drives, community events and the latest news from Reach Groups.
          </p>
        </div>
      </section>

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px 20px', gap: 14 }}>
          <Loader2 size={32} color="#005B66" className="spin-icon" />
          <p style={{ color: '#6B7280', fontSize: 14 }}>Loading announcements...</p>
        </div>
      )}

      {!loading && loadError && (
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <p style={{ color: '#991b1b', fontSize: 15 }}>Couldn't load this page right now. Please refresh or try again shortly.</p>
        </div>
      )}

      {!loading && !loadError && (
        <>
          {/* Events / We Are Hiring */}
          <section style={{ padding: '60px 0', background: '#fff' }}>
            <div className="container">
              <div style={{ display: 'flex', gap: 12, marginBottom: 40 }}>
                {(['upcoming', 'jobs'] as const).map(t => (
                  <button key={t} onClick={() => setTab(t)} style={{
                    padding: '10px 28px', borderRadius: 100, fontSize: 15, fontWeight: 600,
                    fontFamily: 'Poppins, sans-serif', cursor: 'pointer', transition: 'all 0.2s',
                    background: tab === t ? '#005B66' : '#f1f5f9',
                    color: tab === t ? '#fff' : '#374151',
                    border: '2px solid', borderColor: tab === t ? '#005B66' : '#e2e8f0',
                  }}>{t === 'upcoming' ? 'Upcoming Events' : 'We Are Hiring'}</button>
                ))}
              </div>

              {tab === 'upcoming' ? (
                filteredEvents.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6B7280', fontSize: 14, padding: '40px 0' }}>
                    No upcoming events right now — check back soon.
                  </p>
                ) : (
                  <StaggerGroup key="upcoming" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 28 }}>
                    {filteredEvents.map((e, idx) => {
                      const Icon = pickEventIcon(e.category);
                      const isFree = (e.discount || '').toUpperCase().includes('FREE');
                      return (
                        <StaggerItem key={e.id || `${e.title}-${idx}`}>
                          <TiltCard maxTilt={5} scale={1.015} className="tilt-parent" style={{ height: '100%' }}>
                            <div className="hover-lift" style={{ background: '#f7f9fa', borderRadius: 18, overflow: 'hidden', border: '1px solid rgba(0,91,102,0.10)', height: '100%' }}>
                              <div style={{ height: 140, position: 'relative' }}>
                                <SafeImage src={e.image} alt={e.title} />
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(0,20,22,0.15), rgba(0,20,22,0.55))' }} />
                                <div style={{ position: 'absolute', top: 14, left: 14, width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#005B66' }}>
                                  <Icon size={19} />
                                </div>
                                {e.discount && (
                                  <div style={{
                                    position: 'absolute', top: 14, right: 14, padding: '5px 11px', borderRadius: 8,
                                    background: isFree ? '#0B7078' : '#D62828', color: '#fff',
                                    fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 12.5, letterSpacing: '0.3px',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.28)',
                                  }}>{e.discount}</div>
                                )}
                              </div>
                              <div style={{ padding: 24 }}>
                                <div style={{ marginBottom: 12 }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: '#005B66', background: '#e8f4f5', padding: '3px 10px', borderRadius: 6, letterSpacing: '0.5px', fontFamily: 'Poppins, sans-serif' }}>{e.category}</span>
                                </div>
                                <h3 className="sweep-underline" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 17, color: '#1a1a1a', margin: '0 0 6px', display: 'inline-block' }}>{e.title}</h3>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#6B7280', marginBottom: 6 }}>
                                  <CalendarDays size={14} /> {formatEventDate(e.date)}
                                </div>
                                {e.time && (
                                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#6B7280', marginBottom: 6 }}>
                                    <Clock size={14} /> {e.time}
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#6B7280', marginBottom: 14 }}>
                                  <MapPin size={14} /> {e.venue}
                                </div>
                                <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.7, marginBottom: 20 }}>{e.description}</p>
                                <button onClick={() => setRegisterEvent({ id: e.id || e.title, title: e.title })}
                                  className="btn-primary shine" style={{ fontSize: 13, padding: '10px 18px' }}>Register Now <ArrowRight size={14} /></button>
                              </div>
                            </div>
                          </TiltCard>
                        </StaggerItem>
                      );
                    })}
                  </StaggerGroup>
                )
              ) : (
                jobs.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6B7280', fontSize: 14, padding: '40px 0' }}>
                    No current openings — check back soon.
                  </p>
                ) : (
                  <StaggerGroup key="jobs" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 28 }}>
                    {jobs.map((j, idx) => (
                      <StaggerItem key={`${j.title}-${idx}`}>
                        <TiltCard maxTilt={5} scale={1.015} className="tilt-parent" style={{ height: '100%' }}>
                          <div className="hover-lift" style={{ background: '#f7f9fa', borderRadius: 18, overflow: 'hidden', border: '1px solid rgba(0,91,102,0.10)', height: '100%' }}>
                            <div style={{ height: 140, position: 'relative', background: '#e8f4f5' }}>
                              {j.image ? (
                                <SafeImage src={j.image} alt={j.title} />
                              ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Briefcase size={40} color="#005B66" strokeWidth={1.5} />
                                </div>
                              )}
                              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(0,20,22,0.1), rgba(0,20,22,0.45))' }} />
                              <div style={{ position: 'absolute', top: 14, left: 14, width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#005B66' }}>
                                <Briefcase size={19} />
                              </div>
                            </div>
                            <div style={{ padding: 24 }}>
                              <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 17, color: '#1a1a1a', margin: '0 0 12px' }}>{j.title}</h3>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 13, color: '#6B7280', marginBottom: j.experience ? 8 : 0 }}>
                                <GraduationCap size={14} style={{ marginTop: 2, flexShrink: 0 }} /> {j.qualification}
                              </div>
                              {j.experience && (
                                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 13, color: '#6B7280', marginBottom: 14 }}>
                                  <Briefcase size={14} style={{ marginTop: 2, flexShrink: 0 }} /> {j.experience}
                                </div>
                              )}
                              <button onClick={() => setApplyJob(j.title)}
                                className="btn-primary shine" style={{ fontSize: 13, padding: '10px 18px', marginTop: j.experience ? 0 : 14 }}>Apply Now <ArrowRight size={14} /></button>
                            </div>
                          </div>
                        </TiltCard>
                      </StaggerItem>
                    ))}
                  </StaggerGroup>
                )
              )}
            </div>
          </section>

          {/* Blogs — dark section to set "Health Insights" apart from the surrounding white sections */}
          <section className="matte-dark grain" style={{ padding: '72px 0', position: 'relative', overflow: 'hidden' }}>
            <div className="blob float" style={{ top: -100, right: -80, width: 380, height: 380, background: 'radial-gradient(circle, rgba(214,40,40,0.14) 0%, transparent 70%)' }} />
            <div className="blob float" style={{ bottom: -120, left: -80, width: 340, height: 340, background: 'radial-gradient(circle, rgba(11,112,120,0.22) 0%, transparent 70%)', animationDelay: '1s' }} />
            <div className="container" style={{ position: 'relative' }}>
              <div style={{ textAlign: 'center', marginBottom: 48 }}>
                <h2 className="section-title" style={{ color: '#fff', marginTop: 14 }}>Health Tips & Blogs</h2>
              </div>
              {blogs.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Nothing published yet — check back soon.</p>
              ) : (
                <StaggerGroup style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 24 }}>
                  {blogs.map((b, idx) => {
                    const Icon = pickBlogIcon(b.tag);
                    const cardInner = (
                      <>
                        <div className="icon-badge" style={{ width: 52, height: 52, background: '#e8f4f5', color: '#005B66', marginBottom: 16 }}><Icon size={24} /></div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#005B66', background: '#e8f4f5', padding: '3px 10px', borderRadius: 6 }}>{b.tag}</span>
                        <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 16, color: '#1a1a1a', margin: '12px 0 10px', lineHeight: 1.4 }}>{b.title}</h3>
                        <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#9ca3af' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><CalendarDays size={13} /> {b.date}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{b.read} read</span>
                        </div>
                        <div style={{ marginTop: 16, color: '#005B66', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>Read article <ArrowRight size={14} /></div>
                      </>
                    );
                    const cardStyle: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 24, border: '1px solid rgba(0,91,102,0.08)', cursor: 'pointer', height: '100%', display: 'block', textDecoration: 'none' };
                    return (
                      <StaggerItem key={`${b.title}-${idx}`}>
                        <TiltCard maxTilt={5} className="tilt-parent" style={{ height: '100%' }}>
                          {b.url ? (
                            <a href={b.url} target="_blank" rel="noopener noreferrer" className="hover-lift" style={cardStyle}>{cardInner}</a>
                          ) : (
                            <div className="hover-lift" style={cardStyle}>{cardInner}</div>
                          )}
                        </TiltCard>
                      </StaggerItem>
                    );
                  })}
                </StaggerGroup>
              )}
            </div>
          </section>

          {/* Downloads */}
          <section style={{ padding: '60px 0', background: '#fff' }}>
            <div className="container">
              <div style={{ textAlign: 'center', marginBottom: 40 }}>
                <h2 className="section-title">Downloads</h2>
              </div>
              {downloads.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6B7280', fontSize: 14 }}>No downloads available yet.</p>
              ) : (
                <StaggerGroup style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px,1fr))', gap: 20 }}>
                  {downloads.map((d, i) => (
                    <StaggerItem key={`${d.title}-${i}`}>
                      <a
                        href={d.fileurl} target="_blank" rel="noopener noreferrer"
                        style={{ background: '#f7f9fa', borderRadius: 12, padding: 20, border: '1px solid rgba(0,91,102,0.10)', display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer', transition: 'all 0.2s', textDecoration: 'none' }}
                        onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.borderColor = '#005B66'; t.style.background = '#f0fafb'; t.style.transform = 'translateY(-3px)'; }}
                        onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.borderColor = 'rgba(0,91,102,0.10)'; t.style.background = '#f7f9fa'; t.style.transform = 'translateY(0)'; }}>
                        <div className="icon-badge" style={{ width: 44, height: 44, background: '#e8f4f5', color: '#005B66' }}><FileText size={20} /></div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 14, color: '#1a1a1a', marginBottom: 4 }}>{d.title}</div>
                          <div style={{ fontSize: 12, color: '#9ca3af' }}>{d.size}</div>
                        </div>
                        <Download size={19} color="#005B66" />
                      </a>
                    </StaggerItem>
                  ))}
                </StaggerGroup>
              )}
            </div>
          </section>
        </>
      )}

      <EventRegisterModal eventId={registerEvent?.id ?? null} eventTitle={registerEvent?.title ?? null} onClose={() => setRegisterEvent(null)} />
      <EventRegisterModal eventId={registerEvent?.id ?? null} eventTitle={registerEvent?.title ?? null} onClose={() => setRegisterEvent(null)} />
      <JobApplyModal jobTitle={applyJob} onClose={() => setApplyJob(null)} />
      <style>{`
        .spin-icon { animation: spin 0.8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default Announcements;